import { useEffect, useState } from 'react';
import { Hammer, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ConfigItemStatusBadge } from './ConfigItemStatusBadge';
import { categoryLabel } from '../utils/discoveryGrid';
import { projectId } from '../utils/supabase/info';

/**
 * DeliverWorkspace: the Deliver workspace's Deliver item (step 4 of the simplification plan).
 * Every client's build backlog in one place, read here, acted on from the client's Deliver tab
 * (export, documentation and the status moves live there, next to the discovery they came from).
 */

interface DeliverRow {
  companyId: string;
  companyName: string;
  itemId: string;
  name: string;
  category: string;
  /** The item's one lifecycle status (docs/CONFIG_ITEM_LIFECYCLE.md). */
  status: string;
  ballInCourt: string | null;
}

interface DeliverCompany {
  id: string;
  name: string;
  readyForBuild: number;
  inBuild: number;
  readyForReview: number;
  approved: number;
  total: number;
}

interface DeliverWorkspaceProps {
  accessToken: string;
  onOpenClient: (companyId: string) => void;
}

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;
/** Rows shown at once; the rest are reached from the client's page. */
export const ROWS_SHOWN = 60;

export function DeliverWorkspace({ accessToken, onOpenClient }: DeliverWorkspaceProps) {
  const [rows, setRows] = useState<DeliverRow[] | null>(null);
  const [companies, setCompanies] = useState<DeliverCompany[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BASE}/consultant/deliver`, { headers: { Authorization: `Bearer ${accessToken}` } });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
        if (cancelled) return;
        setRows(Array.isArray(body.rows) ? body.rows : []);
        setCompanies(Array.isArray(body.companies) ? body.companies : []);
        setError(null);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Could not load the build backlog');
        setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [accessToken]);

  const withWork = companies.filter((c) => c.total > 0);

  return (
    <div className="space-y-6" data-testid="deliver-workspace">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Hammer className="size-4" aria-hidden="true" /> Build backlog across clients</CardTitle>
          <CardDescription>Every client's items that are locked for build or already in build. Open a client to move an item, export its configuration or write up the handoff.</CardDescription>
        </CardHeader>
        <CardContent>
          {rows === null ? (
            <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="size-4 animate-spin" aria-hidden="true" /> Loading…</div>
          ) : error ? (
            <div className="text-sm text-gray-600">{error}</div>
          ) : withWork.length === 0 ? (
            <div className="text-sm text-gray-600">Nothing is ready for build yet. Items arrive here when a client's discovery is locked.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {withWork.map((c) => (
                <div key={c.id} className="rounded-lg border p-4 flex flex-col gap-2" data-testid="deliver-company">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold">{c.name}</span>
                    <Button size="sm" variant="outline" onClick={() => onOpenClient(c.id)}>Open</Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    {c.readyForBuild > 0 && <Badge className="bg-blue-100 text-blue-800 border-blue-200">{c.readyForBuild} ready for build</Badge>}
                    {c.inBuild > 0 && <Badge variant="secondary">{c.inBuild} in build</Badge>}
                    {c.readyForReview > 0 && <Badge className="bg-amber-100 text-amber-800 border-amber-200">{c.readyForReview} ready for review</Badge>}
                    {c.approved > 0 && <Badge className="bg-green-100 text-green-800 border-green-200">{c.approved} approved</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {rows && rows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Items</CardTitle>
            <CardDescription>Ready for build first. The status moves on the client's Deliver tab.{rows.length > ROWS_SHOWN && !showAll ? ` Showing ${ROWS_SHOWN} of ${rows.length}.` : ''}</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ball in court</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(showAll ? rows : rows.slice(0, ROWS_SHOWN)).map((r) => (
                  <TableRow key={`${r.companyId}:${r.itemId}`} data-testid="deliver-row">
                    <TableCell><button type="button" className="text-indigo-700 hover:underline" onClick={() => onOpenClient(r.companyId)}>{r.companyName}</button></TableCell>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-gray-600">{categoryLabel(r.category)}</TableCell>
                    <TableCell><ConfigItemStatusBadge status={r.status} size="sm" /></TableCell>
                    <TableCell className="text-gray-600 capitalize">{r.ballInCourt ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {rows.length > ROWS_SHOWN && !showAll && (
              <div className="pt-3"><Button variant="outline" size="sm" onClick={() => setShowAll(true)}>Show all {rows.length}</Button></div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
